import {
  useSessionStore,
} from "~/stores/session";
import {
  useLocaleStore,
} from "~/stores/locale";
import {
  checkBlockedAccessAfterApiError,
} from "~/composables/useBlockedAccess";

export type ApiFetchOptions = {
  headers?: Record<string, string>;
} & Record<string, any>;

const joinUrl = (base: string, path: string): string => {
  if (!base) {
    return path;
  }
  if (base.endsWith("/") && path.startsWith("/")) {
    return `${base.slice(0, -1)}${path}`;
  }
  if (!base.endsWith("/") && !path.startsWith("/")) {
    return `${base}/${path}`;
  }
  return `${base}${path}`;
};

export const useApiFetch = () => {
  const config = useRuntimeConfig();
  const sessionStore = useSessionStore();

  return async <T>(path: string, options: ApiFetchOptions = {}): Promise<T> => {
    const {headers, ...rest} = options;

    const mockFlag = String(config.public.mockApi ?? "").toLowerCase().trim();
    const mockEnabled = mockFlag === "1" || mockFlag === "true";

    const baseUrl = mockEnabled ? "" : String(config.public.apiBaseUrl ?? "").trim();
    const basePath = String(config.public.apiBasePath ?? "/api").trim() || "/api";

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    const pathWithPrefix = normalizedPath.startsWith(basePath)
      ? normalizedPath
      : joinUrl(basePath, normalizedPath);

    const url = joinUrl(baseUrl, pathWithPrefix);

    const token = await sessionStore.getAccessToken();
    const mergedHeaders: Record<string, string> = {
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(headers ?? {}),
    };

    try {
      const response = await $fetch(url, {
        headers: mergedHeaders,
        ...rest,
      });

      return response as unknown as T;
    } catch (error) {
      void checkBlockedAccessAfterApiError({
        error,
        token,
        apiBaseUrl: baseUrl,
        apiBasePath: basePath,
        mockEnabled,
        requestPath: normalizedPath,
      });
      throw error;
    }
  };
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const localeStore = useLocaleStore();
  const e = error as any;
  const message = e?.data?.message || e?.message || fallback;
  return localeStore.t(message);
};
